import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import { exportExcelSection, exportPDFSection } from '../common/ExportUtils';
import { openFilePreview } from '../common/FilePreviewModal';
import ConfirmDialog from '../common/ConfirmDialog';
import TableScrollContainer from '../common/TableScrollContainer';
import { getAuthHeaders } from '../../auth';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext';
import DnRoReportTab from './DnRoReportTab';
import JobForm from '../documentation/JobForm';
import '../documentation/JobList.css';

const API_BASE = '/api/documentation/jobs';

const fundRequestIcon = (r, released) => r.is_fund_request && !r.fund_request_released && !released && (r.airport_remarks || '').toUpperCase() !== 'RELEASED' ? (
  <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginRight: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />
) : null;
const DUTY_OPTIONS = ['PAID', 'NOT PAID'];

const CopyCell = ({ text, children }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
        if (!val || val === '-') return;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(val).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            });
        } else {
            const ta = document.createElement('textarea');
            ta.value = val;
            ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
            document.body.removeChild(ta);
        }
    };
    return (
        <span className="copy-cell">
            {children}
            <button
                type="button"
                className={`copy-icon-btn${copied ? ' copied' : ''}`}
                title={copied ? 'Copied!' : 'Copy'}
                onClick={handleCopy}
                onMouseDown={e => e.stopPropagation()}
            >
                {copied
                    ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                }
            </button>
        </span>
    );
};

const toUpperDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value.toString().toUpperCase();
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return value.toString().toUpperCase();
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// --- Airport Staff Dialog ---
const AirportStaffDialog = ({ open, row, staffList, currentStaffIds, onClose, onAssign, hasPermission }) => {
  const [selectedStaff, setSelectedStaff] = useState(currentStaffIds || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedStaff(currentStaffIds || []);
  }, [currentStaffIds, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { airport_staff_ids: selectedStaff };
      console.log('Saving staff for job:', row.idf_no, 'Staff IDs:', selectedStaff, 'Payload:', payload);
      const res = await fetch(`${API_BASE}/${row.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to assign staff.');
      toast.success('Staff assigned successfully!');
      onAssign();
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const sortedStaffList = useMemo(() => {
    return [...staffList].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [staffList]);

  return (
    <div className="modal-overlay" style={{ display: open ? 'flex' : 'none' }}>
      <div className="dialog-container" style={{ width: 500 }}>
        <div className="dialog-header">
          <h3>Assign Staff for Job: {row?.idf_no}</h3>
        </div>
        <div className="dialog-body">
          <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>Select staff members to assign to this job as RELEASED.</p>
          <MultiSelectDropdown
            id="airport-staff-select"
            options={sortedStaffList.map(s => s.name)}
            selectedValues={sortedStaffList.filter(s => selectedStaff.includes(s.id)).map(s => s.name)}
            onChange={selectedNames => {
              const ids = sortedStaffList.filter(s => selectedNames.includes(s.name)).map(s => s.id);
              setSelectedStaff(ids);
            }}
            placeholder="Select Staff"
            maxMenuHeight={220}
          />
        </div>
        <div className="dialog-footer">
          <button type="button" className="dialog-btn dialog-btn-secondary" onClick={onClose}>Cancel</button>
          {hasPermission('AIRPORT REPORTS', 'can_edit') && (
            <button type="button" className="dialog-btn dialog-btn-primary" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AirportMainTab = ({
  hasPermission,
  fixedRemarkFilter,
  onOpenDailyReports,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idfFilter, setIdfFilter] = useState('');
  const [search, setSearch] = useState('');
  const [terminalFilter, setTerminalFilter] = useState('');
  const [remarkFilter, setRemarkFilter] = useState(fixedRemarkFilter || '');
  const [dropdowns, setDropdowns] = useState({ terminals: [], transports: [], remarks: [], staff: [] });
  const [savingId, setSavingId] = useState(null);
  const [generatingDaily, setGeneratingDaily] = useState(false);
  const [previewingDaily, setPreviewingDaily] = useState(false);
  const [staffDialog, setStaffDialog] = useState({ open: false, row: null, staffIds: [] });
  const [editingReleasedRemarkId, setEditingReleasedRemarkId] = useState(null);
  const [tempReleasedRemark, setTempReleasedRemark] = useState('');
  const [tempReleasedDate, setTempReleasedDate] = useState('');
  const [drafts, setDrafts] = useState({});
  const [editingCells, setEditingCells] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [jobFormModal, setJobFormModal] = useState({ isOpen: false, job: null });

  // Drag-and-drop state
  const [dragSrcId, setDragSrcId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const isReleasedReport = (fixedRemarkFilter || '').toUpperCase() === 'RELEASED';

  const fetchDropdowns = useCallback(async () => {
    try {
      const [res, portRes] = await Promise.all([
        fetch(`${API_BASE}/airport-dropdowns/`, { headers: getAuthHeaders(false) }),
        fetch(`${API_BASE}/port-dropdowns/`, { headers: getAuthHeaders(false) })
      ]);
      if (!res.ok) return;
      const data = await res.json();
      const portData = portRes.ok ? await portRes.json() : {};
      setDropdowns({
        terminals: data.terminals || [],
        transports: data.transports || [],
        remarks: data.remarks || [],
        staff: portData.staff || [],
      });
    } catch {
      // Non-blocking fetch.
    }
  }, []);

  const fetchRowsForRemarkFilter = useCallback(async (nextRemarkFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nextRemarkFilter) params.append('remark_filter', nextRemarkFilter);
      const res = await fetch(`${API_BASE}/airport-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch airport report.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setRemarkFilter(nextRemarkFilter);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch airport report.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    await fetchRowsForRemarkFilter(remarkFilter);
  }, [fetchRowsForRemarkFilter, remarkFilter]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);


  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      rows.forEach(r => {
        next[r.id] = {
          ...(next[r.id] || {}),
          eta: r.eta || '',
          dutyPaid: !!r.duty_paid || (r.duty_status || '').toUpperCase() === 'PAID',
          terminal: r.terminal || '',
          transport: r.transport || '',
          remarks: r.raw_remarks || '',
          releaseDate: r.release_date || '',
        };
      });
      return next;
    });
  }, [rows]);

  const getCellKey = (rowId, field) => `${rowId}:${field}`;

  const isEditingCell = (rowId, field) => !!editingCells[getCellKey(rowId, field)];

  const startEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        eta: row.eta || '',
        dutyPaid: !!row.duty_paid || (row.duty_status || '').toUpperCase() === 'PAID',
        terminal: row.terminal || '',
        transport: row.transport || '',
        remarks: row.raw_remarks || '',
        releaseDate: row.release_date || '',
      },
    }));
    setEditingCells(prev => ({ ...prev, [getCellKey(row.id, field)]: true }));
  };

  const cancelEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        eta: row.eta || '',
        dutyPaid: !!row.duty_paid || (row.duty_status || '').toUpperCase() === 'PAID',
        terminal: row.terminal || '',
        transport: row.transport || '',
        remarks: row.raw_remarks || '',
        releaseDate: row.release_date || '',
      },
    }));
    setEditingCells(prev => {
      const next = { ...prev };
      delete next[getCellKey(row.id, field)];
      return next;
    });
  };

  const saveEditCell = async (row, field, payload) => {
    const saved = await updateJobField(row, payload);
    if (saved) {
      setEditingCells(prev => {
        const next = { ...prev };
        delete next[getCellKey(row.id, field)];
        return next;
      });
    }
  };

  // --- Drag-and-drop handlers ---
  const handleDragStart = useCallback((e, jobId) => {
    setDragSrcId(jobId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, jobId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (jobId !== dragSrcId) setDragOverId(jobId);
  }, [dragSrcId]);

  const handleDragEnd = useCallback(() => {
    setDragSrcId(null);
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(async (e, targetId) => {
    e.preventDefault();
    if (!dragSrcId || dragSrcId === targetId) { handleDragEnd(); return; }
    const newRows = [...rows];
    const srcIdx = newRows.findIndex(r => r.id === dragSrcId);
    const tgtIdx = newRows.findIndex(r => r.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) { handleDragEnd(); return; }
    const movingDown = srcIdx < tgtIdx;
    const [srcRow] = newRows.splice(srcIdx, 1);
    const newTgtIdx = newRows.findIndex(r => r.id === targetId);
    newRows.splice(movingDown ? newTgtIdx + 1 : newTgtIdx, 0, srcRow);
    setRows(newRows);
    handleDragEnd();
    const orders = newRows.map((r, i) => ({ id: r.id, sort_order: i }));
    try {
      const res = await fetch(`${API_BASE}/update-airport-sort-order/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ orders }),
      });
      if (!res.ok) throw new Error('Failed to save order.');
    } catch {
      toast.error('Failed to save row order.');
      fetchRows();
    }
  }, [dragSrcId, rows, setRows, handleDragEnd, fetchRows]);

  const clearAllFilters = async () => {
    setIdfFilter('');
    setSearch('');
    setTerminalFilter('');
    const nextRemark = fixedRemarkFilter || '';
    setRemarkFilter(nextRemark);
    await fetchRowsForRemarkFilter(nextRemark);
  };

  const updateJobField = async (row, payload) => {
    if (!hasPermission('AIRPORT REPORTS', 'can_edit')) {
      toast.error('You do not have permission to edit Airport Report.');
      return false;
    }

    setSavingId(row.id);
    try {
      const res = await fetch(`${API_BASE}/${row.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to update record.');
      await fetchDropdowns();
      await fetchRowsForRemarkFilter(remarkFilter);
      return true;
    } catch (e) {
      toast.error(e.message || 'Failed to update record.');
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handlePreviewDailyReport = async () => {
    setPreviewingDaily(true);
    try {
      const res = await fetch(`${API_BASE}/preview-daily-airport-report/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate preview.');
      }
      const blob = await res.blob();
      if (!blob.size) throw new Error('The preview PDF is empty.');
      const blobUrl = URL.createObjectURL(blob);
      openFilePreview(blobUrl, `Preview_Airport_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to generate preview.');
    } finally {
      setPreviewingDaily(false);
    }
  };

  const executeGenerateDailyReport = async () => {
    setGeneratingDaily(true);
    try {
      setIdfFilter('');
      setSearch('');
      setTerminalFilter('');
      setRemarkFilter('');
      // Clear cache on daily report generation
      await fetchRowsForRemarkFilter('');

      const res = await fetch(`${API_BASE}/generate-daily-airport-report/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ report_date: new Date().toISOString().split('T')[0] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate daily airport report.');
      }
      toast.success('Daily Airport Report generated and saved successfully.');
      if (onOpenDailyReports) onOpenDailyReports();
    } catch (e) {
      toast.error(e.message || 'Failed to generate daily airport report.');
    } finally {
      setGeneratingDaily(false);
    }
  };

  const handleGenerateDailyReport = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Generate Daily Report',
      message: 'Generating this report will overwrite any previously generated report for today. Are you sure you would like to generate this report now?',
      onConfirm: () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        executeGenerateDailyReport();
      },
      onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false }),
    });
  };

  const handleDeliveryOrderClick = async (row) => {
    try {
      const res = await fetch(`${API_BASE}/${row.id}/delivery-order-pdf/`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to generate delivery order.');
      const blob = await res.blob();
      if (!blob.size) throw new Error('Empty PDF.');
      const url = URL.createObjectURL(blob);
      openFilePreview(url, `Delivery_Order_${(row.idf_no || row.job_no || row.id)}.pdf`);
    } catch (e) {
      toast.error(e.message || 'Failed to generate delivery order.');
      console.error(e);
    }
  };

  const openJobFormModal = async (jobId) => {
    try {
      const res = await fetch(`${API_BASE}/${jobId}/`, { headers: getAuthHeaders(false) });
      if (!res.ok) { toast.error('Failed to load job details'); return; }
      const data = await res.json();
      setJobFormModal({ isOpen: true, job: data });
    } catch (e) {
      toast.error('Error loading job');
    }
  };

  const closeJobFormModal = () => {
    setJobFormModal({ isOpen: false, job: null });
  };

  const filteredRows = useMemo(() => {
    let data = [...rows];
    if (idfFilter.trim()) {
      const needle = idfFilter.trim().toUpperCase();
      data = data.filter(r => (r.idf_no || '').toUpperCase().includes(needle));
    }
    if (terminalFilter) {
      data = data.filter(r => (r.terminal || '').toUpperCase() === terminalFilter.toUpperCase());
    }
    if (search.trim()) {
      const needle = search.trim().toUpperCase();
      data = data.filter(r =>
        [
          r.idf_no,
          r.job_no,
          r.awb,
          r.airline,
          r.flight_number,
          r.importer,
          r.terminal,
          r.transport,
          r.remarks,
        ].some(field => field && field.toString().toUpperCase().includes(needle))
      );
    }
    return data;
  }, [rows, idfFilter, terminalFilter, search]);

  const releasedSort = useSortableTable(filteredRows, { key: 'release_date', direction: 'descending' });

  const acceptedRows = filteredRows.filter(r => {
    const status = (r.assessment_status || '').toUpperCase();
    return status === 'ACCEPTED' || status === 'SENT';
  });
  const pendingRows = filteredRows.filter(r => {
    const status = (r.assessment_status || '').toUpperCase();
    return status !== 'ACCEPTED' && status !== 'SENT';
  });

  const remarkOptions = useMemo(() => {
    const values = dropdowns.remarks.map(r => (typeof r === 'string' ? r : r.text)).filter(Boolean);
    if (!values.includes('RELEASED')) values.push('RELEASED');
    return Array.from(new Set(values)).map(v => ({ id: v, text: v }));
  }, [dropdowns.remarks]);

  const terminalOptions = useMemo(() => {
    const values = dropdowns.terminals.map(t => (typeof t === 'string' ? t : t.text)).filter(Boolean);
    return Array.from(new Set(values)).map(v => ({ id: v, text: v }));
  }, [dropdowns.terminals]);

  const transportOptions = useMemo(() => {
    const values = dropdowns.transports.map(t => (typeof t === 'string' ? t : t.text)).filter(Boolean);
    return Array.from(new Set(values)).map(v => ({ id: v, text: v }));
  }, [dropdowns.transports]);

  const getRemarkOptionsForRow = (row) => {
    const isDutyPaid = (row.duty_status || '').toUpperCase() === 'PAID';
    return remarkOptions.filter(opt => opt.id !== 'RELEASED' || isDutyPaid);
  };

  const normalizeCreatedValue = (term) => {
    const value = (term || '').trim();
    if (!value) return null;
    return { id: value, text: value };
  };

  const createTerminalOption = async (term) => {
    const option = normalizeCreatedValue(term);
    if (!option) return null;
    setDropdowns(prev => ({
      ...prev,
      terminals: [...prev.terminals, option],
    }));
    return option;
  };

  const createTransportOption = async (term) => {
    const option = normalizeCreatedValue(term);
    if (!option) return null;
    setDropdowns(prev => ({
      ...prev,
      transports: [...prev.transports, option],
    }));
    return option;
  };

  const createRemarkOption = async (term) => {
    const option = normalizeCreatedValue(term);
    if (!option) return null;
    setDropdowns(prev => ({
      ...prev,
      remarks: [...prev.remarks, option],
    }));
    return option;
  };

  const startEditReleasedRemark = (row, focusDate = false) => {
    setEditingReleasedRemarkId(row.id);
    setTempReleasedRemark(row.raw_remarks || '');
    setTempReleasedDate(row.release_date || '');
    if (focusDate) {
      setTimeout(() => {
        const el = document.querySelector(`input[data-release-date-for="${row.id}"]`);
        if (el) el.focus();
      }, 50);
    }
  };

  const cancelEditReleasedRemark = () => {
    setEditingReleasedRemarkId(null);
    setTempReleasedRemark('');
    setTempReleasedDate('');
  };

  const saveReleasedRemark = async (row) => {
    if (!hasPermission('AIRPORT REPORTS', 'can_delete')) return;
    const nextRemark = (tempReleasedRemark || '').trim();
    if (nextRemark === 'RELEASED' && !tempReleasedDate) {
      toast.error('Please enter a Release Date before saving RELEASED.');
      return;
    }
    if (nextRemark === 'RELEASED') {
      if (row.is_fund_request && !row.fund_request_released) {
        toast.error('Cannot Release. Awaiting Funds from Client');
        return;
      }
      setStaffDialog({
        open: true,
        row,
        staffIds: row.airport_staff_ids || []
      });
      return;
    }
    const payload = { airport_remarks: nextRemark || null };
    if (nextRemark === 'RELEASED' && tempReleasedDate) payload.release_date = tempReleasedDate;
    await updateJobField(row, payload);
    cancelEditReleasedRemark();
  };

  const getPendingSectionRemark = (r) => {
    const jobStatus = Array.isArray(r.job_status) ? r.job_status : [];
    if (jobStatus.some(s => String(s).toUpperCase() === 'COMPLETE')) {
      return r.assessment_status_label || r.assessment_status || '';
    }
    return r.status_remark || '';
  };

  const renderRows = (dataRows, options = {}) => {
    const { readOnly = false, showStaff = false, remarksButtonOnly = false, pendingSection = false, sortable = false, requestSort, getSortIndicator } = options;
    const baseColSpan = pendingSection ? 11 : 13;
    const colSpan = baseColSpan + (showStaff ? 1 : 0) + (remarksButtonOnly ? 1 : 0);

    const renderHeader = (label, key) => (
      sortable ? (
        <th onClick={() => requestSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          {label} {getSortIndicator(key)}
        </th>
      ) : (
        <th>{label}</th>
      )
    );

    return (
    <TableScrollContainer>
      <table className="jobs-table" style={{ overflow: 'visible' }}>
        <thead>
          <tr>
            {renderHeader('IDF NO', 'idf_no')}
            {renderHeader('JOB NO', 'job_no')}
            {renderHeader('AWB', 'awb')}
            {renderHeader('PACKAGES', 'packages')}
            {renderHeader('WEIGHT', 'weight')}
            {renderHeader('AIRLINE', 'airline')}
            {renderHeader('FLIGHT NUMBER', 'flight_number')}
            {renderHeader('ETA', 'eta')}
            {renderHeader('IMPORTER', 'importer')}
            {!pendingSection && renderHeader('DUTY PAID', 'duty_status')}
            {renderHeader('TERMINAL', 'terminal')}
            {!pendingSection && renderHeader('TRANSPORT', 'transport')}
            {renderHeader('REMARKS', 'remarks')}
            {showStaff && renderHeader('STAFF', 'airport_staff_names')}
            {remarksButtonOnly && renderHeader('RELEASED DATE', 'release_date')}
          </tr>
        </thead>
        <tbody>
          {loading && (<tr><td colSpan={colSpan} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
          {!loading && dataRows.length === 0 && (<tr><td colSpan={colSpan} className="no-data">No records found.</td></tr>)}
          {!loading && dataRows.map(r => (
            <tr
              key={r.id}
              draggable={hasPermission('AIRPORT REPORTS', 'can_edit') && !fixedRemarkFilter && !readOnly}
              onDragStart={hasPermission('AIRPORT REPORTS', 'can_edit') && !fixedRemarkFilter && !readOnly ? (e) => handleDragStart(e, r.id) : undefined}
              onDragOver={(e) => handleDragOver(e, r.id)}
              onDrop={(e) => handleDrop(e, r.id)}
              onDragEnd={handleDragEnd}
              style={{
                cursor: hasPermission('AIRPORT REPORTS', 'can_edit') && !fixedRemarkFilter && !readOnly ? 'grab' : undefined,
                ...(dragOverId === r.id && dragSrcId !== r.id ? { outline: '2px dashed #3f6b32', backgroundColor: '#e8f5e9' } : {}),
                ...(dragSrcId === r.id ? { opacity: 0.5 } : {}),
              }}
            >
              <td style={{ userSelect: 'text' }}><CopyCell text={r.idf_no}>{toUpperDisplay(r.idf_no)}</CopyCell></td>
              <td style={{ userSelect: 'text' }}><CopyCell text={r.job_no}><a href="#" onClick={(e) => { e.preventDefault(); openJobFormModal(r.id); }} style={{ textDecoration: 'underline', color: '#007bff' }}>{toUpperDisplay(r.job_no)}</a></CopyCell></td>
              <td style={{ userSelect: 'text' }}>
                {!pendingSection && !readOnly && hasPermission('AIRPORT REPORTS', 'can_edit') && r.awb ? (
                  <a href="#" onClick={(e) => { e.preventDefault(); handleDeliveryOrderClick(r); }} style={{ textDecoration: 'underline', color: '#007bff' }}>
                    {toUpperDisplay(r.awb)}
                  </a>
                ) : (
                  toUpperDisplay(r.awb)
                )}
              </td>
              <td style={{ userSelect: 'text' }}><CopyCell text={r.packages != null ? String(r.packages) : ''}>{r.packages ?? '-'}</CopyCell></td>
              <td style={{ userSelect: 'text' }}><CopyCell text={r.weight != null ? String(r.weight) : ''}>{formatNumber(r.weight)}</CopyCell></td>
              <td style={{ userSelect: 'text' }}><CopyCell text={r.airline}>{toUpperDisplay(r.airline)}</CopyCell></td>
              <td style={{ userSelect: 'text' }}><CopyCell text={r.flight_number}>{toUpperDisplay(r.flight_number)}</CopyCell></td>
              <td>
                {!readOnly && hasPermission('AIRPORT REPORTS', 'can_edit') ? isEditingCell(r.id, 'eta') ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="date"
                      className="search-input"
                      value={drafts[r.id]?.eta || ''}
                      onChange={e => setDrafts(prev => ({
                        ...prev,
                        [r.id]: { ...(prev[r.id] || {}), eta: e.target.value },
                      }))}
                      disabled={savingId === r.id}
                      style={{ minWidth: 145 }}
                    />
                    <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveEditCell(r, 'eta', { airport_eta: (drafts[r.id]?.eta || '').trim() || null })}><i className="fa fa-save" /></button>
                    <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'eta')}><i className="fa fa-times" /></button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CopyCell text={r.eta}><span>{toUpperDisplay(r.eta)}</span></CopyCell>
                    <button type="button" className="icon-btn" title="Edit ETA" onClick={() => startEditCell(r, 'eta')}><i className="fa fa-pencil" /></button>
                  </div>
                ) : (
                  <CopyCell text={r.eta}>{toUpperDisplay(r.eta)}</CopyCell>
                )}
              </td>
              <td style={{ userSelect: 'text' }}><CopyCell text={r.importer}><span><strong>{toUpperDisplay(r.importer)}</strong>{!isReleasedReport && r.is_fund_request && !r.fund_request_released && (r.airport_remarks || '').toUpperCase() !== 'RELEASED' && <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginLeft: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />}</span></CopyCell></td>
              {!pendingSection && <td style={{ textAlign: 'center' }}>
                {!readOnly && hasPermission('AIRPORT REPORTS', 'can_edit') ? isEditingCell(r.id, 'duty') ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={!!drafts[r.id]?.dutyPaid}
                      onChange={e => setDrafts(prev => ({
                        ...prev,
                        [r.id]: { ...(prev[r.id] || {}), dutyPaid: e.target.checked },
                      }))}
                      disabled={savingId === r.id}
                      style={{ margin: 0 }}
                    />
                    <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveEditCell(r, 'duty', { duty_paid: !!drafts[r.id]?.dutyPaid })}><i className="fa fa-save" /></button>
                    <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'duty')}><i className="fa fa-times" /></button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span style={{ fontSize: '13px', fontWeight: '500' }}>{toUpperDisplay(r.duty_status) === 'PAID' ? 'PAID' : 'NOT PAID'}</span>
                    <button type="button" className="icon-btn" title="Edit Duty Status" onClick={() => startEditCell(r, 'duty')}><i className="fa fa-pencil" /></button>
                  </div>
                ) : (
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{toUpperDisplay(r.duty_status) === 'PAID' ? 'PAID' : 'NOT PAID'}</span>
                )}
              </td>}
              <td>
                {!readOnly && hasPermission('AIRPORT REPORTS', 'can_edit') ? isEditingCell(r.id, 'terminal') ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Select2Dropdown
                      options={terminalOptions}
                      value={drafts[r.id]?.terminal || ''}
                      onChange={val => setDrafts(prev => ({
                        ...prev,
                        [r.id]: { ...(prev[r.id] || {}), terminal: val || '' },
                      }))}
                      valueKey="id"
                      displayKey="text"
                      placeholder="Select or create"
                      allowCreate
                      onCreateTag={createTerminalOption}
                      clearable
                      style={{ minWidth: 170 }}
                      disabled={savingId === r.id}
                    />
                    <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveEditCell(r, 'terminal', { airport_terminal: drafts[r.id]?.terminal || null })}><i className="fa fa-save" /></button>
                    <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'terminal')}><i className="fa fa-times" /></button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CopyCell text={r.terminal}><span>{toUpperDisplay(r.terminal)}</span></CopyCell>
                    <button type="button" className="icon-btn" title="Edit Terminal" onClick={() => startEditCell(r, 'terminal')}><i className="fa fa-pencil" /></button>
                  </div>
                ) : (
                  <CopyCell text={r.terminal}>{toUpperDisplay(r.terminal)}</CopyCell>
                )}
              </td>
              {!pendingSection && <td>
                {!readOnly && hasPermission('AIRPORT REPORTS', 'can_edit') ? isEditingCell(r.id, 'transport') ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Select2Dropdown
                      options={transportOptions}
                      value={drafts[r.id]?.transport || ''}
                      onChange={val => setDrafts(prev => ({
                        ...prev,
                        [r.id]: { ...(prev[r.id] || {}), transport: val || '' },
                      }))}
                      valueKey="id"
                      displayKey="text"
                      placeholder="Select or create"
                      allowCreate
                      onCreateTag={createTransportOption}
                      clearable
                      style={{ minWidth: 170 }}
                      disabled={savingId === r.id}
                    />
                    <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveEditCell(r, 'transport', { airport_transport: drafts[r.id]?.transport || null })}><i className="fa fa-save" /></button>
                    <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'transport')}><i className="fa fa-times" /></button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CopyCell text={r.transport}><span>{toUpperDisplay(r.transport)}</span></CopyCell>
                    <button type="button" className="icon-btn" title="Edit Transport" onClick={() => startEditCell(r, 'transport')}><i className="fa fa-pencil" /></button>
                  </div>
                ) : (
                  <CopyCell text={r.transport}>{toUpperDisplay(r.transport)}</CopyCell>
                )}
              </td>}
              <td style={{ minWidth: 220 }}>
                {pendingSection ? (
                  <CopyCell text={getPendingSectionRemark(r)}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(getPendingSectionRemark(r))}</span></CopyCell>
                ) : remarksButtonOnly ? (
                  editingReleasedRemarkId === r.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select2Dropdown
                            options={getRemarkOptionsForRow(r)}
                            value={tempReleasedRemark}
                            onChange={v => { setTempReleasedRemark(v); if (v !== 'RELEASED') setTempReleasedDate(''); }}
                            valueKey="id"
                            displayKey="text"
                            placeholder="SELECT REMARK"
                            clearable
                            style={{ width: '100%' }}
                            disabled={savingId === r.id}
                          />
                        </div>
                        <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveReleasedRemark(r)}><i className="fa fa-save" /></button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelEditReleasedRemark}><i className="fa fa-times" /></button>
                      </div>
                      {tempReleasedRemark === 'RELEASED' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>Release Date <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                            value={tempReleasedDate}
                            onChange={e => setTempReleasedDate(e.target.value)}
                            required
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.remarks}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</span></CopyCell>
                      {hasPermission('AIRPORT REPORTS', 'can_delete') && (
                        <button type="button" className="icon-btn" title="Edit Remarks" onClick={() => startEditReleasedRemark(r)}><i className="fa fa-pencil" /></button>
                      )}
                    </div>
                  )
                ) : !readOnly && hasPermission('AIRPORT REPORTS', 'can_edit') ? isEditingCell(r.id, 'remarks') ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Select2Dropdown
                          options={getRemarkOptionsForRow(r)}
                          value={drafts[r.id]?.remarks || ''}
                          onChange={val => setDrafts(prev => ({
                            ...prev,
                            [r.id]: { ...(prev[r.id] || {}), remarks: val || '', releaseDate: val !== 'RELEASED' ? '' : (prev[r.id]?.releaseDate || '') },
                          }))}
                          valueKey="id"
                          displayKey="text"
                          placeholder="SELECT REMARK"
                          clearable
                          style={{ width: '100%' }}
                          disabled={savingId === r.id}
                        />
                      </div>
                      <button
                        className="icon-btn save-btn"
                        title="Save"
                        disabled={savingId === r.id}
                        onClick={() => {
                          const remark = drafts[r.id]?.remarks || '';
                          if (remark === 'RELEASED' && !(drafts[r.id]?.releaseDate)) {
                            toast.error('Please enter a Release Date before saving RELEASED.');
                            return;
                          }
                          if (remark === 'RELEASED') {
                            if (r.is_fund_request && !r.fund_request_released) {
                              toast.error('Cannot Release. Awaiting Funds from Client');
                              return;
                            }
                            setStaffDialog({ open: true, row: r, staffIds: r.airport_staff_ids || [] });
                          } else {
                            saveEditCell(r, 'remarks', { airport_remarks: remark || null });
                          }
                        }}
                      >
                        <i className="fa fa-save" />
                      </button>
                      <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'remarks')}><i className="fa fa-times" /></button>
                    </div>
                    {(drafts[r.id]?.remarks || '') === 'RELEASED' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>Release Date <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                          value={drafts[r.id]?.releaseDate || ''}
                          onChange={e => setDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), releaseDate: e.target.value } }))}
                          required
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CopyCell text={r.remarks}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</span></CopyCell>
                    <button type="button" className="icon-btn" title="Edit Remarks" onClick={() => startEditCell(r, 'remarks')}><i className="fa fa-pencil" /></button>
                  </div>
                ) : (
                  <CopyCell text={r.remarks}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</CopyCell>
                )}
              </td>
              {showStaff && <td style={{ userSelect: 'text' }}><CopyCell text={(r.airport_staff_names || []).join(', ')}>{toUpperDisplay((r.airport_staff_names || []).join(', '))}</CopyCell></td>}
              {remarksButtonOnly && (
                <td style={{ whiteSpace: 'nowrap', fontWeight: 600, userSelect: 'text' }}>
                  {editingReleasedRemarkId === r.id ? (
                    <input
                      data-release-date-for={r.id}
                      type="date"
                      className="form-control"
                      style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                      value={tempReleasedDate}
                      onChange={e => setTempReleasedDate(e.target.value)}
                    />
                  ) : (
                    <CopyCell text={r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : ''}>
                      {r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : '—'}
                    </CopyCell>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </TableScrollContainer>
    );
  };

  return (
    <>
      <div className="toolbar" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto' }}>
          <input className="search-input" placeholder="IDF Search" value={idfFilter} onChange={e => setIdfFilter(e.target.value)} style={{ minWidth: 140 }} />
          <Select2Dropdown
            options={[{ id: '', text: 'All Terminals' }, ...terminalOptions]}
            value={terminalFilter}
            onChange={val => setTerminalFilter(val || '')}
            valueKey="id"
            displayKey="text"
            style={{ minWidth: 180 }}
            placeholder="Terminal Filter"
          />
          <Select2Dropdown
            options={[{ id: '', text: 'All Remarks' }, ...remarkOptions]}
            value={remarkFilter}
            onChange={val => setRemarkFilter(val || '')}
            valueKey="id"
            displayKey="text"
            style={{ minWidth: 180 }}
            placeholder="Remarks Filter"
            isDisabled={!!fixedRemarkFilter}
          />
          <input className="search-input" placeholder="General Search" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 220 }} />
          <button className="add-btn" style={{ background: '#6c757d', whiteSpace: 'nowrap' }} onClick={clearAllFilters}>Clear All Filters</button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="add-btn" onClick={fetchRows}>Refresh</button>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {!fixedRemarkFilter && (
              <>
                <button className="add-btn" style={{ background: '#e67e22' }} onClick={handlePreviewDailyReport} disabled={previewingDaily || generatingDaily}>
                  {previewingDaily ? 'Loading Preview...' : 'Preview Daily Report'}
                </button>
                <button className="add-btn" style={{ background: '#3f6b32' }} onClick={handleGenerateDailyReport} disabled={generatingDaily || previewingDaily}>
                  {generatingDaily ? 'Generating...' : 'Generate Daily Report'}
                </button>
              </>
            )}
            <button className="add-btn" style={{ background: '#17a2b8' }} onClick={() => exportPDFSection(null, 'AirportReport_All', { sections: [
              { title: 'Airport Report - Assessment Accepted / Sent', data: acceptedRows },
              { title: 'Pending Assessments', data: pendingRows, excludeColumns: ['duty_paid', 'duty_status', 'transport'] }
            ] })}>Download PDF</button>
            <button className="add-btn" style={{ background: '#007bff' }} onClick={() => exportExcelSection(filteredRows, 'AirportReport_All')}>Download Excel</button>
          </div>
        </div>
      </div>

      {!fixedRemarkFilter && (
        <>
          <div style={{ marginBottom: 26 }}>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#d9ecff', borderRadius: 6, padding: 8, fontSize: 16 }}>
              AIRPORT REPORT - ASSESSMENT ACCEPTED / SENT
            </div>
            {renderRows(acceptedRows)}
          </div>

          <div>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#ffe3bf', borderRadius: 6, padding: 8, fontSize: 16 }}>
              PENDING ASSESSMENTS
            </div>
            {renderRows(pendingRows, { pendingSection: true })}
          </div>
        </>
      )}

      {fixedRemarkFilter && (
        <div>
          <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#c2fcc2', borderRadius: 6, padding: 8, fontSize: 16 }}>
            RELEASED REPORT
          </div>
          {renderRows(releasedSort.items, { readOnly: true, showStaff: true, remarksButtonOnly: true, sortable: true, requestSort: releasedSort.requestSort, getSortIndicator: releasedSort.getSortIndicator })}
        </div>
      )}

      {staffDialog.open && (
        <AirportStaffDialog
          open={staffDialog.open}
          row={staffDialog.row}
          staffList={dropdowns.staff}
          currentStaffIds={staffDialog.staffIds}
          onClose={() => setStaffDialog({ open: false, row: null, staffIds: [] })}
          onAssign={async () => {
            setStaffDialog({ open: false, row: null, staffIds: [] });
            if (staffDialog.row) {
              const relDate = tempReleasedDate || drafts[staffDialog.row.id]?.releaseDate || '';
              const payload = { airport_remarks: 'RELEASED' };
              if (relDate) payload.release_date = relDate;
              await updateJobField(staffDialog.row, payload);
              setEditingCells(prev => {
                const next = { ...prev };
                delete next[getCellKey(staffDialog.row.id, 'remarks')];
                return next;
              });
            }
            cancelEditReleasedRemark();
          }}
          hasPermission={hasPermission}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
      />

      {jobFormModal.isOpen && (
        <div className="modal-overlay">
          <div className="dialog-container" style={{
            width: '95%',
            maxWidth: '1400px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Job Details</h3>
              <button className="icon-btn" onClick={closeJobFormModal} title="Close">
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="dialog-body" style={{ padding: 0, overflowY: 'auto', flex: 1 }}>
              {jobFormModal.job ? (
                <JobForm
                  mode="view"
                  initialData={jobFormModal.job}
                  onClose={closeJobFormModal}
                />
              ) : (
                <div style={{ padding: 20 }}>Loading...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function formatDailyDate(dateStr, createdAt) {
  const pad = n => n.toString().padStart(2, '0');
  let datePart = dateStr || '';
  if (datePart) {
    const d = new Date(datePart);
    if (!isNaN(d)) datePart = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  let timePart = '';
  if (createdAt) {
    const t = new Date(createdAt);
    if (!isNaN(t)) timePart = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
  }
  return { datePart, timePart };
}

const DailyAirportReportsTab = ({ hasPermission }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [singleDate, setSingleDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const handleViewPdf = useCallback(async (row) => {
    try {
      const res = await fetch(row.view_pdf_url, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load PDF preview.');

      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error('The generated PDF file is empty.');

      const blobUrl = URL.createObjectURL(blob);
      const previewWindow = openFilePreview(blobUrl, `Daily_Airport_Report_${row.date}.pdf`);
      if (previewWindow && previewWindow.addEventListener) {
        previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
      } else {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
      }
    } catch (e) {
      toast.error(e.message || 'Could not open PDF preview.');
    }
  }, []);

  const handleDownload = useCallback(async (url, filename) => {
    try {
      const res = await fetch(url, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Download failed.');
      }
      const blob = await res.blob();
      if (!blob.size) throw new Error('Downloaded file is empty.');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e.message || 'Download failed.');
    }
  }, []);

  const fetchDailyReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (singleDate) {
        params.append('date', singleDate);
      } else {
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
      }
      const res = await fetch(`${API_BASE}/daily-airport-reports/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch daily airport reports.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch daily airport reports.');
    } finally {
      setLoading(false);
    }
  }, [singleDate, fromDate, toDate]);

  useEffect(() => {
    fetchDailyReports();
  }, [fetchDailyReports]);


  if (!hasPermission('AIRPORT REPORTS', 'can_view')) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Daily Airport Reports.</p></div>;
  }

  return (
    <div>
      <div className="toolbar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Single Date</label>
          <input type="date" className="search-input" value={singleDate} onChange={e => setSingleDate(e.target.value)} title="Single Date" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Date Range</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" className="search-input" value={fromDate} onChange={e => setFromDate(e.target.value)} title="From Date" />
            <span style={{ color: '#666', fontWeight: 700 }}>To</span>
            <input type="date" className="search-input" value={toDate} onChange={e => setToDate(e.target.value)} title="To Date" />
          </div>
        </div>
        <button className="add-btn" onClick={fetchDailyReports}>Apply Date Filter</button>
        <button className="add-btn" style={{ background: '#6c757d' }} onClick={() => { setSingleDate(''); setFromDate(''); setToDate(''); }}>Clear Date Filter</button>
      </div>

      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th>DATE</th>
              <th>GENERATED BY</th>
              <th>VIEW PDF</th>
              <th>DOWNLOAD PDF</th>
              <th>DOWNLOAD EXCEL</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={5} className="no-data">No daily reports found.</td></tr>)}
            {!loading && rows.map(r => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(() => { const { datePart, timePart } = formatDailyDate(r.date, r.created_at); return (<span>{datePart}{timePart && <><br /><span style={{ fontSize: 11, color: '#555' }}>{timePart}</span></>}</span>); })()}
                </td>
                <td>{toUpperDisplay(r.generated_by)}</td>
                <td>
                  <button type="button" className="icon-btn" title="View PDF" onClick={() => handleViewPdf(r)}>
                    <i className="fa fa-eye" />
                  </button>
                </td>
                <td>
                  <button type="button" className="icon-btn" title="Download PDF" onClick={() => handleDownload(r.download_pdf_url, `Daily_Airport_Report_${r.date}.pdf`)}>
                    <i className="fa fa-download" />
                  </button>
                </td>
                <td>
                  <button type="button" className="icon-btn" title="Download Excel" onClick={() => handleDownload(r.download_excel_url, `Daily_Airport_Report_${r.date}.xlsx`)}>
                    <i className="fa fa-download" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
    </div>
  );
};

export default function AirportReport() {
  const { hasPermission: rawHasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('airportReport');
  const [refreshKey, setRefreshKey] = useState(0);
  const [tabCounts, setTabCounts] = useState({
    airportReport: null,
    dailyAirportReports: null,
    releasedReport: null,
    dnRoReport: null,
  });

  const updateTabCount = useCallback((tab, count) => {
    setTabCounts(prev => ({ ...prev, [tab]: count }));
  }, []);

  const hasPermission = useCallback((module, action) => {
    if (module === 'AIRPORT REPORTS') {
      return rawHasPermission('AIRPORT REPORTS', action) || rawHasPermission('ADMIN AIRPORT REPORTS', action);
    }
    return rawHasPermission(module, action);
  }, [rawHasPermission]);

  useEffect(() => {
    if (!hasPermission('AIRPORT REPORTS', 'can_view')) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/airport-report/?dn_ro_only=true&count_only=true`, { headers: getAuthHeaders(false) });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) updateTabCount('dnRoReport', data.count ?? null);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [hasPermission, updateTabCount]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    if (!hasPermission('AIRPORT REPORTS', 'can_view')) {
      return;
    }
    if (!['airportReport', 'releasedReport', 'dailyAirportReports', 'dnRoReport'].includes(activeTab)) {
      setActiveTab('airportReport');
    }
  }, [activeTab, hasPermission]);

  if (!hasPermission('AIRPORT REPORTS', 'can_view')) {
    return (
      <div className="permission-denied-container" style={{ padding: '20px' }}>
        <h2>Permission Denied</h2>
        <p>You do not have permission to view Airport Reports.</p>
      </div>
    );
  }

  return (
    <div className="job-list-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Airport Reports</h2>
        <button onClick={handleRefresh} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 16 }}>
        <button className={`custom-tab ${activeTab === 'airportReport' ? 'active' : ''}`} onClick={() => { setActiveTab('airportReport'); handleRefresh(); }}>
          Airport Report {tabCounts.airportReport !== null && `(${tabCounts.airportReport})`}
        </button>
        <button className={`custom-tab ${activeTab === 'dailyAirportReports' ? 'active' : ''}`} onClick={() => setActiveTab('dailyAirportReports')}>
          Daily Airport Report {tabCounts.dailyAirportReports !== null && `(${tabCounts.dailyAirportReports})`}
        </button>
        <button className={`custom-tab ${activeTab === 'releasedReport' ? 'active' : ''}`} onClick={() => { setActiveTab('releasedReport'); handleRefresh(); }}>
          Released Report {tabCounts.releasedReport !== null && `(${tabCounts.releasedReport})`}
        </button>
        <button
          className={`custom-tab ${activeTab === 'dnRoReport' ? 'active' : ''} ${tabCounts.dnRoReport ? 'flash-tab' : ''}`}
          onClick={() => setActiveTab('dnRoReport')}
        >
          Pending DN/RO {tabCounts.dnRoReport !== null && `(${tabCounts.dnRoReport})`}
        </button>
        <style>{`
          @keyframes flash-tab {
            0%, 100% { background-color: #e9f5ff; color: #333; box-shadow: 0 0 0 rgba(0, 0, 0, 0); }
            50% { background-color: #003366; color: #fff; box-shadow: 0 0 10px rgba(0, 51, 102, 0.6); }
          }
          .custom-tab.flash-tab {
            animation: flash-tab 1.2s ease-in-out infinite;
            border-color: #003366;
          }
        `}</style>
      </div>

      <div className="tab-content">
        {activeTab === 'airportReport' && (
          <AirportMainTab
            hasPermission={hasPermission}
            onOpenDailyReports={() => setActiveTab('dailyAirportReports')}
            refreshKey={refreshKey}
          />
        )}

        {activeTab === 'dailyAirportReports' && (
          <DailyAirportReportsTab hasPermission={hasPermission} onCountChange={(count) => updateTabCount('dailyAirportReports', count)} />
        )}

        {activeTab === 'dnRoReport' && hasPermission('AIRPORT REPORTS', 'can_view') && (
          <DnRoReportTab hasPermission={hasPermission} onCountChange={(count) => updateTabCount('dnRoReport', count)} reportType="airport" />
        )}

        {activeTab === 'releasedReport' && (
          <AirportMainTab
            hasPermission={hasPermission}
            fixedRemarkFilter="RELEASED"
            onOpenDailyReports={() => setActiveTab('dailyAirportReports')}
            refreshKey={refreshKey}
          />
        )}
      </div>
    </div>
  );
}
